/**
 * PriorityPill - small text pill that surfaces the queue-rank axis next to
 * the ScoreDeltaChip's score-lift axis.
 *
 * BPA rows label by framework criticality (SHALL / SHOULD / MAY). The 1:1
 * mapping crit↔SHALL / high↔SHOULD / low↔MAY is enforced in
 * BpaActionMapper.MapSeverity, so deriving on the client is safe.
 *
 * Drift and identity rows label with the generic severity vocabulary
 * (CRIT / HIGH / MED / LOW / ROUTINE) — their severities aren't framework
 * prescriptions, so SHALL/SHOULD/MAY would misrepresent the axis.
 *
 * Tone colors mirror SEVERITY_NUMERAL_COLOR in PriorityQueue so the pill
 * pairs visually with the rank numeral.
 */

import clsx from 'clsx'
import type { Pillar, Severity } from '@/lib/posture-brief'

const BPA_LABEL: Partial<Record<Severity, string>> = {
  crit: 'SHALL',
  high: 'SHOULD',
  low: 'MAY',
}

const GENERIC_LABEL: Record<Severity, string> = {
  crit: 'CRIT',
  high: 'HIGH',
  med: 'MED',
  low: 'LOW',
  routine: 'ROUTINE',
}

const TONE_CLASS: Record<Severity, string> = {
  crit:    'bg-red-500/15 text-red-400',
  high:    'bg-secondarySand-500/15 text-secondarySand-300',
  med:     'bg-secondaryBlue-500/15 text-secondaryBlue-300',
  low:     'bg-primary-500/15 text-primary-400',
  routine: 'bg-slate-500/15 text-slate-400',
}

const BPA_TOOLTIP: Partial<Record<Severity, string>> = {
  crit: "Must-fix per the frameworks. That's why it ranks high. The GS chip beside it is separate: it's the score lift you actually get from closing this.",
  high: "Recommended by the frameworks. Ranks below the must-fix items. The GS chip shows the score lift you'd get from closing this.",
  low:  "Optional or aspirational. Sits at the bottom of the queue. The GS chip shows the score lift you'd get from closing this.",
}

const GENERIC_TOOLTIP: Record<Severity, string> = {
  crit:    'Top priority. Fix this first.',
  high:    'High priority. Worth fixing soon.',
  med:     'Medium priority.',
  low:     'Low priority.',
  routine: 'Minor finding. Ranked by tie-breaks.',
}

export interface PriorityPillProps {
  severity: Severity
  pillar: Pillar
}

export default function PriorityPill({ severity, pillar }: PriorityPillProps) {
  // Use SHALL/SHOULD/MAY only when the pillar is BPA AND the severity maps
  // to a framework tier. Falls back to generic labels for med / routine
  // (which BpaActionMapper never produces today, but we stay defensive).
  const useBpaLabel = pillar === 'bpa' && BPA_LABEL[severity] != null
  const label = useBpaLabel ? BPA_LABEL[severity]! : GENERIC_LABEL[severity]
  const tooltip = useBpaLabel ? BPA_TOOLTIP[severity]! : GENERIC_TOOLTIP[severity]

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5',
        TONE_CLASS[severity],
        'text-[10px] font-bold uppercase tracking-[0.08em]',
        'whitespace-nowrap',
      )}
      title={tooltip}
      aria-label={tooltip}
    >
      {label}
    </span>
  )
}
